import { isPlatformServer } from "@angular/common";
import { Component, Inject, Input, PLATFORM_ID, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ApiAvAsset, ApiLiveStream, ApiPodcast, ApiProduction, ApiShow, ApiTuneInProvider, ApiUser, ApiVOD } from "@tytapp/api";
import { sleep, SubscriptionSet } from "@tytapp/common";
import { NoJsMediaPlayerComponent } from "../no-js-media-player/no-js-media-player.component";
import { PlaybackSession } from "../playback";
import { PlayerComponent } from "../player/player.component";

@Component({
    selector: 'tyt-media-view',
    templateUrl: './media-view.component.html',
    styleUrls: ['./media-view.component.scss']
})
export class MediaViewComponent {
    constructor(
        @Inject(PLATFORM_ID)
        private platformId: any,
        private readonly activatedRoute: ActivatedRoute
    ) {
        this.viewReady = new Promise((res, rej) => this.fireViewReady = res);
    }

    @Input() enableEditor: boolean = true;

    get showEditLink() {
        return this.enableEditor && this.user && this.user.staff;
    }

    private viewReady: Promise<void>;
    private fireViewReady: Function;

    _production: ApiProduction;
    _item: ApiVOD | ApiPodcast | ApiLiveStream;

    get currentlyPlayingVOD(): ApiVOD {
        return this.item as ApiVOD;
    }

    get currentlyPlayingPodcast(): ApiPodcast {
        return this.item as ApiPodcast;
    }

    @Input() get production() { return this._production; }
    @Input() get item() { return this._item; }
    @Input() inEditor = false;

    updateItem(item: ApiVOD | ApiPodcast | ApiLiveStream) {
        this._item = item;
    }

    set production(value) {
        if (this._production === value)
            return;
        this._production = value;
    }

    set item(value) {
        if (this._item === value)
            return;
        this._item = value;
        setTimeout(() => {
            this.load()
        });
    }

    get show() { return this.production?.show; }
    @Input() mode: 'episodes' | 'clips' | 'podcasts' = 'episodes';
    @Input() user: ApiUser;

    startingVodID: string;

    paywallVisible = false;
    subscriptions: SubscriptionSet = new SubscriptionSet();

    async load() {
        this.checkSSRPlayer();

        const filteredPlaylistItem = this.playlist.find(podcast => podcast.id === this.activatedRoute.snapshot.params.vod);

        let vod = this._item || filteredPlaylistItem;

        if (isPlatformServer(this.platformId) && this.nativeVideoPlayerComponent) {
            this.nativeVideoPlayerComponent.play(vod.asset);
        } else {
            await this.viewReady;
            await this.playItem(vod, this.playlist);
            //this.airplayService.loadMedia(vod);
        }
    }

    ngAfterViewInit() {
        this.fireViewReady();
    }

    get playlist(): ApiProduction['full_length_vods'] | ApiProduction['full_length_podcasts'] | ApiProduction['vod_clips']  {
        if (!this.production)
            return undefined;

        if (this.mode === 'episodes')
            return this.production.full_length_vods;
        else if (this.mode === 'clips')
            return this.production.vod_clips;
        else if (this.mode === 'podcasts')
            return this.production.full_length_podcasts;
        else
            throw new Error(`No valid playlist for mode '${this.mode}'`);

    }

    @ViewChild('player', { static: true })
    player: PlayerComponent;

    get topicIdentifier() {
        if (!this.production || !this.item)
            return null;

        return `vod_${this.item.id}`;
    }

    get topicTitle() {
        if (!this.production || !this.item)
            return null;

        return this.item.description;
    }

    get loginOrJoinUrl() {
        if (!this.user)
            return `/login`;
        return `/join`;
    }

    get entitled() {
        if (!this.user)
            return false;

        if (this.user.entitled)
            return true;

        if (this.user.membership && this.user.membership.entitled)
            return true;

        return false;
    }

    async playItem(vod, playlist): Promise<PlaybackSession> {
        if (typeof window === 'undefined')
            return;

        this._item = vod;
        await sleep(1);

        if (this.mode == 'podcasts')
            return await this.player.playPodcast(this.production, vod, this.mode, playlist);
        else
            return await this.player.playVOD(this.production, vod, this.mode, playlist);
    }

    @ViewChild(NoJsMediaPlayerComponent, { static: true })
    nativeVideoPlayerComponent: NoJsMediaPlayerComponent;

    // Set to true to allow no-JS media player
    enableNoJsMediaPlayer = false;

    async checkSSRPlayer() {
        if (this.nativeVideoPlayerComponent)
            await this.nativeVideoPlayerComponent.viewReady;
    }

    getPodcastProviders(show: ApiShow): ApiTuneInProvider[] {
        if (!show || !show.tune_ins)
            return [];

        let podcastTuneIn = show.tune_ins.find(x => x.type == 'podcast');

        if (!podcastTuneIn)
            return [];

        return podcastTuneIn.destinations;
    }
}